import { registerLocaleData } from '@angular/common';
import localeBe from '@angular/common/locales/nl-BE';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule, inject, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import Bugsnag from '@bugsnag/js';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import { ComponentsModule } from '@campus/components';
import {
  AuthService,
  AuthServiceInterface,
  DalModule,
  DalState,
  MethodStyleQueries,
  loadAndResolveMethodStyleForBook,
} from '@campus/dal';
import { EnvironmentModule } from '@campus/environment';
import { METHOD_STYLE_TOKEN } from '@campus/features/theming/shared';
import { AuthenticationGuard, GuardsModule, PermissionGuard } from '@campus/guards';
import { LearnosityModule } from '@campus/learnosity';
import { SearchModule } from '@campus/search';
import { SharedModule } from '@campus/shared';
import { UiModule } from '@campus/ui';
import { DEFAULT_BUGSNAG_IGNORED_ERRORS, UtilsModule, createBugsnagErrorFilter } from '@campus/utils';
import { Store } from '@ngrx/store';
import player from 'lottie-web';
import { configureBufferSize } from 'ngrx-undo-v2';
import { provideLottieOptions } from 'ngx-lottie';
import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AppEffectsModule } from './app-effects.module';
import { AppRoutingModule } from './app-routing.module';
import { AppStoreModule } from './app-store.module';
import { AppTokenModule } from './app-token.module';
import { AppComponent } from './app.component';
import {
  EduContentSearchResultComponent,
  TocInfoPipe,
} from './components/edu-content-search-result/edu-content-search-result.component';
import { LoadingComponent } from './components/loading/loading.component';
import { AppHeaderComponent } from './components/navigation/app-header.component';
import { AppRailComponent } from './components/navigation/app-rail.component';
import { HomePageGuard } from './guard/home-page.guard';
import { TermsPrivacyPopupGuard } from './guard/terms-privacy-popup/terms-privacy-popup.guard';
import { AppWrapperComponent } from './wrapper.component';

// if you want to update the buffer (which defaults to 100)
configureBufferSize(150);

registerLocaleData(localeBe, 'nl-BE');

let { release: appVersion, stage: releaseStage } = environment;
if (!appVersion || appVersion === '{RELEASE_BRANCH}') {
  // development or tests during build
  appVersion = 'development';
  releaseStage = 'development';
}

Bugsnag.start({
  apiKey: '8ba57070795519f07a624072d35b3eb3',
  appVersion,
  releaseStage,
  enabledReleaseStages: ['staging', 'production'],
  onError: createBugsnagErrorFilter(DEFAULT_BUGSNAG_IGNORED_ERRORS),
});

export function errorHandlerFactory() {
  if (releaseStage === 'development') return new ErrorHandler();
  return new BugsnagErrorHandler();
}

// Note we need a separate function as it's required
// by the AOT compiler.
export function playerFactory() {
  return player;
}

const disableAnimations =
  !('animate' in document.documentElement) || (navigator && /iPhone OS (8|9|10|11|12|13)_/.test(navigator.userAgent));

@NgModule({
  declarations: [
    AppComponent,
    EduContentSearchResultComponent,
    TocInfoPipe,
    LoadingComponent,
    AppRailComponent,
    AppHeaderComponent,
    AppWrapperComponent,
  ],
  imports: [
    environment.envModules || [],
    RouterModule,
    UiModule,
    SearchModule,
    UtilsModule,
    MatTooltipModule,
    BrowserModule,
    MatDialogModule,
    SharedModule,
    EnvironmentModule.forRoot({
      environmentAlertsFeature: environment.features.alerts,
      environmentMessagesFeature: environment.features.messages,
      environmentErrorManagementFeature: environment.features.errorManagement,
      environmentGlobalSearch: environment.features.globalSearch,
      environmentCollectionManagementFeature: environment.features.collectionManagement,
      environmentIconMapping: environment.iconMapping,
      environmentTextMapping: environment.textMapping,
      environmentWebsite: environment.website,
      environmentLogout: environment.afterLogout,
      environmentLogin: environment.login,
      environmentProfile: environment.profile,
      environmentTermPrivacy: environment.termPrivacy,
      environmentApi: environment.api,
      environmentSsoSettings: environment.sso,
      environmentTesting: environment.testing,
      environmentUi: environment.ui,
      environmentAssets: environment.assets,
      environmentFavoritesFeature: environment.features.favorites,
      environmentHistoryFeature: environment.features.history,
      environmentSearchModes: environment.searchModes,
      environmentPlatform: environment.platform,
      environmentSvgMapping: environment.svgMapping,
      environmentGroupsFeature: environment.features.groups,
      environmentScoreMapping: environment.results?.scoreMapping,
      environmentResultsShowStars: environment.results?.showStars,
      environmentTime: environment.time,
      environmentCrossOrigin: environment.crossOrigin,
      environmentMethodsFeature: environment.features.methods,
      environmentNewsFeature: environment.features.news,
      environmentFreshworksFeature: environment.features.freshworks,
      environmentGoogleAnalytics: environment.googleAnalytics,
      environmentTaskFeature: environment.taskFeature,
      environmentScormFeature: environment.features?.scorm,
      environmentBasePath: environment.basePath,
    }),
    BrowserAnimationsModule.withConfig({ disableAnimations }),
    DalModule.forRoot({
      apiBaseUrl: environment.api.APIBase,
      apiV2BaseUrl: environment.api.APIV2Base,
      campusApiBaseUrl: environment.api.APICampusBase,
      eventBroker: environment.api.eventBroker,
    }),
    GuardsModule,
    AppRoutingModule,
    AppTokenModule,
    AppEffectsModule,
    AppStoreModule,
    ComponentsModule,
    FormsModule,
    ReactiveFormsModule,
    LearnosityModule,
  ],
  providers: [
    provideLottieOptions({ player: playerFactory }),
    Title,
    provideAppInitializer(() => {
      const initializerFn = (
        (authService: AuthServiceInterface) => () =>
          authService.getCurrent().pipe(
            tap((user) => {
              if (user?.id) {
                Bugsnag.setUser(user.id.toString());
              }
            })
          )
      )(inject(AuthService));
      return initializerFn();
    }),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    { provide: MAT_DATE_LOCALE, useValue: 'nl-BE' },
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: { position: 'above' } },
    HomePageGuard,
    TermsPrivacyPopupGuard,
    AuthenticationGuard,
    PermissionGuard,
    {
      provide: METHOD_STYLE_TOKEN,
      useFactory: (store: Store<DalState>) => (bookId: number) => {
        return of({ bookId }).pipe(
          loadAndResolveMethodStyleForBook(bookId)(store, undefined),
          switchMap(() => store.select(MethodStyleQueries.getByEduContentBookId(bookId)))
        );
      },
      deps: [Store],
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppWrapperComponent],
  exports: [RouterModule],
})
export class AppModule {}
